import moment from 'moment';
import { getResortNameByType, getAllCookies } from './utils';

const pushToDataLayer = (data = {}) => {
  if (!data || typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

const getDefaultProperties = (data = {}) => {
  const {
    resId,
    siteCode,
    arrivalDate,
    departureDate,
    numOfGuests,
    numOfChildren,
    numOfToddlers,
    numOfInfants,
    numOfAdults,
    numOfUnderFifteens,
  } = getAllCookies();
  const calculatedDaysToArrival = moment(arrivalDate).diff(moment(), 'day');
  const formattedDaysToArrival = calculatedDaysToArrival > 0 ? calculatedDaysToArrival + 1 : 0;
  const commonData = {
    bookingRef: resId.toString(),
    resortCode: siteCode.toUpperCase(),
    resortName: getResortNameByType(siteCode),
    arrivalDate: moment(arrivalDate).format('YYYY-MM-DD'),
    arrivalSeason: moment(arrivalDate).format('YYYY'),
    departureDate,
    daysToArrival: formattedDaysToArrival,
    stayDuration: moment(departureDate).diff(moment(arrivalDate), 'day'),
    numAdults: numOfAdults,
    numChildren: numOfChildren,
    numToddlers: numOfToddlers,
    numInfants: numOfInfants,
    numGuests: numOfGuests,
    numUnderFifteens: numOfUnderFifteens,
    partyConfig: `${numOfAdults} adults|${numOfChildren} children|${numOfToddlers} toddlers|${numOfInfants} infants`,
  };
  return { ...data, ...commonData };
};

const getLoginMessage = () => 'unauthorized error';
const getTimeSlotIsFullMessage = () => 'sorry but this slot is full';
const getNotFoundBookingMessage = () => 'sorry your booking could not be found';

const get400ErrorMessage = (errorMessage, { url = '' }) => {
  if (url.includes('booking/search')) return getNotFoundBookingMessage();
  if (errorMessage.includes('session')) return getLoginMessage();
  return 'something went wrong';
};

const get500ErrorMessage = (errorMessage) => {
  if (errorMessage === 'insufficient capacity remaining for activity') return getTimeSlotIsFullMessage();
  return 'something went wrong';
};

const getErrorMessage = (error) => {
  const { response = {}, request, config = {}, message = '' } = error;
  if (message && message.length > 0) {
    return message;
  }
  if (response) {
    const { data = {}, status } = response;
    const [errorFromApi = {}] = data.Errors || [];
    const { Message = '' } = errorFromApi;
    const errorMessage = Message.toLowerCase();

    if (status === 401) return getLoginMessage();
    if (status === 400) return get400ErrorMessage(errorMessage, config);
    if (status === 500) return get500ErrorMessage(errorMessage);
    return 'something went wrong';
  }
  if (request) return 'not responding';
  return 'sorry we can not execute this action at the moment';
};

export const logInOutPushGTM = (
  type = 'login',
  result = 'error',
  error = {},
) => {
  let data = {
    event: 'visitor interaction',
    interactionName: `day planner ${type}`,
  };
  data = getDefaultProperties(data);
  data.successFlag = result;
  data.errorType = result !== 'success' ? getErrorMessage(error) : null;
  pushToDataLayer(data);
};

export const notFoundBookingGTM = (rawBookingRef = '', arrivalDate = '') => {
  const data = {
    event: 'visitor interaction',
    interactionName: 'day planner login',
    successFlag: 'error',
    errorType: 'sorry your booking could not be found',
    bookingRef: rawBookingRef,
    arrivalDate: moment(arrivalDate).format('YYYY-MM-DD'),
  };
  pushToDataLayer(data);
};

export const bookMealTimesGTM = (
  eventType = 'breakfast',
  type = 'book',
  result = 'error',
  startTime = '',
  endTime = '',
  eventId = '',
  error = {},
) => {
  let data = {
    event: 'visitor interaction',
    interactionName: `day planner ${type} event slot`,
  };
  data = getDefaultProperties(data);
  data.successFlag = result;
  if (result !== 'success') data.errorType = getErrorMessage(error);
  data.eventType = 'meals';
  data.eventName = eventType;
  data.eventTime = `${moment(startTime, 'HH:mm a').format('HHmm')} - ${moment(endTime, 'HH:mm a').format('HHmm')}`;
  data.eventId = eventId.toString().toLowerCase();
  pushToDataLayer(data);
};

export const bookEntertainmentGTM = (
  eventType = 'swimming',
  type = 'book',
  result = 'error',
  name = '',
  eventDate = '',
  startTime = '',
  endTime = '',
  eventId = '',
  error = {},
) => {
  let data = {
    event: 'visitor interaction',
    interactionName: `day planner ${type} event slot`,
  };
  data = getDefaultProperties(data);
  data.successFlag = result;
  if (result !== 'success') data.errorType = getErrorMessage(error);
  data.eventType = eventType;
  data.eventName = name.toLowerCase();
  data.eventDate = moment(eventDate).format('YYYY-MM-DD');
  data.eventTime = `${moment(startTime, 'HH:mm a').format('HHmm')} - ${moment(endTime, 'HH:mm a').format('HHmm')}`;
  data.eventId = eventId.toString().toLowerCase();
  pushToDataLayer(data);
};

export const editBookingGTM = (
  eventType = 'meals',
  result = 'error',
  name = '',
  eventDate = '',
  startTime = '',
  endTime = '',
  eventId = '',
  error = {},
) => {
  let data = {
    event: 'visitor interaction',
    interactionName: 'day planner edit event slot',
  };
  data = getDefaultProperties(data);
  data.successFlag = result;
  if (result !== 'success') data.errorType = getErrorMessage(error);
  data.eventType = eventType;
  data.eventName = name.toLowerCase();
  if (eventType !== 'meals') data.eventDate = moment(eventDate).format('YYYY-MM-DD'); // we don't know the exact date in this case, because dining will be every day
  data.eventTime = `${moment(startTime, 'HH:mm a').format('HHmm')} - ${moment(endTime, 'HH:mm a').format('HHmm')}`;
  data.eventId = eventId.toString().toLowerCase();
  pushToDataLayer(data);
};

export const virtualPageView = (pageType = '', pageSubType = '', pageName = '') => {
  const data = {
    event: 'virtual page view',
    pageType,
    pageSubType,
    pageName,
  };
  pushToDataLayer(data);
};

export const sendActivityDetailVirtualPage = (name = '', previousNameToVirtualPage = '', setPreviousNameToVirtualPage) => {
  if (!name) return;
  if (name === previousNameToVirtualPage) return;
  virtualPageView('plan my day', 'book entertainment', `/ga-virtual/plan-my-day/book-entertainment/${name.toLowerCase().replace(' ', '-')}-availability`);
  setPreviousNameToVirtualPage(name);
};
